import { useState, useEffect } from 'react';
import { styled, Typography, Box, IconButton, Divider, TextField, MenuItem, Button } from '@mui/material';
import { IconX } from '@tabler/icons';
import { useTheme } from '@mui/material/styles';
import { Filter } from 'types/pagination';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { format } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters, resetFilters } from '../../store/slices/filter';
import { RootState } from '../../store';

export interface globalFilterProps {
    onClose: () => void;
    filters?: Filter[];
    setFilters?: ((val: Filter[]) => void) | undefined;
    availabeFilters?: any;
    showTitle?: Boolean;
}

export interface labelProps {
    label: string;
    icon: string;
    options: Array<{ type: string; name: string; label: string }>;
}

export interface numberProps {
    label: string;
    icon: string;
    options: Array<{
        type: string;
        name: string;
        label: string;
        min?: string;
        max?: string;
        step?: string;
    }>;
}

export interface selectProps {
    label: string;
    icon: string;
    options: Array<{
        type: string;
        name: string;
        label: string;
        values: { [key: string]: string };
    }>;
}
export interface boxProps {
    label: string;
    icon: string;
    options: Array<{
        type: string;
        name: string;
        label: string;
        values: { [key: string]: { name: string; checked?: boolean } };
    }>;
}

export interface datetimeProps {
    label: string;
    icon: string;
    options: Array<{
        type: string;
        name: string;
        label: string;
    }>;
}

const GlobalFilter = (props: globalFilterProps) => {
    const { onClose, availabeFilters, showTitle } = props;
    const theme = useTheme();
    const [labelSection, setLabelSection] = useState<labelProps>();
    const [numberSection, setNumberSection] = useState<numberProps>();
    const [selectSection, setSelectSection] = useState<selectProps>();
    const [datetimeSection, setDatetimeSection] = useState<datetimeProps>();
    const dispatch = useDispatch();
    const savedFilters = useSelector((state: RootState) => state.filters.filters);
    const [newFilters, setNewFilters] = useState<Filter[]>(savedFilters);
    const [resetPending, setResetPending] = useState(false); // State to track pending reset

    const handleApplyFilters = () => {
        onClose();
        if (resetPending) {
            dispatch(resetFilters());
            setResetPending(false);
        } else {
            dispatch(setFilters(newFilters));
        }
    };

    const handleResetFilters = () => {
        setNewFilters([]); // Clear local filters
        setResetPending(true); // Mark reset as pending
    };

    useEffect(() => {
        setNewFilters(savedFilters);
    }, [savedFilters]);

    useEffect(() => {
        availabeFilters.forEach((element: any) => {
            const data: any = element.items;
            switch (element.title) {
                case 'label':
                    setLabelSection(data);
                    break;
                case 'number':
                    setNumberSection(data);
                    break;
                case 'select':
                    setSelectSection(data);
                    break;
                case 'datetime':
                    setDatetimeSection(data);
                    break;
            }
        });
    }, [availabeFilters]);

    const TitleText = styled(Typography)({
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '24px',
        textAlign: 'left',
        margin: 0
    });

    const SubText = styled(Typography)({
        fontSize: '16px'
    });

    const ButtonStyled = styled(Button)({
        height: '44px',
        width: '100px',
        fontSize: '12px'
    });

    return (
        <>
            <Box mt="12px" display="flex" justifyContent="space-between" alignItems="center">
                <TitleText variant="h3">
                    <FormattedMessage id="filters" />
                </TitleText>
                <IconButton onClick={onClose}>
                    <IconX height="14px" width="14px" />
                </IconButton>
            </Box>
            {labelSection && (
                <>
                    <Box mt="28px" mb="24px">
                        {showTitle && (
                            <SubText variant="body1">
                                <FormattedMessage id="label" />
                            </SubText>
                        )}
                        {labelSection?.options.map((item, index) => (
                            <TextField
                                key={`text-field-${index}`}
                                sx={{ mt: 2 }}
                                fullWidth
                                label={<FormattedMessage id={item.label} values={{ indicator: '' }} />}
                                value={newFilters.find((f) => f.name === item.name)?.value || ''}
                                onChange={(e) => {
                                    const removedKeyFilter = newFilters.filter((obj) => obj.name !== item.name);
                                    setNewFilters([...removedKeyFilter, { name: item.name, value: e.target.value }]);
                                }}
                            />
                        ))}
                    </Box>
                    <Divider />
                </>
            )}
            {numberSection && (
                <>
                    <Box mt="28px" mb="24px">
                        <SubText variant="body2">Number</SubText>
                        {numberSection?.options.map((item, index) => (
                            <TextField
                                key={`text-field-number${index}`}
                                sx={{ mt: 2 }}
                                fullWidth
                                label={<FormattedMessage id={item.label} />}
                                type="number"
                                value={newFilters.find((f) => f.name === item.name)?.value || ''}
                                onChange={(e) => {
                                    const removedKeyFilter = newFilters.filter((obj) => obj.name !== item.name);
                                    setNewFilters([...removedKeyFilter, { name: item.name, value: e.target.value }]);
                                }}
                            />
                        ))}
                    </Box>
                    <Divider />
                </>
            )}
            {selectSection && selectSection?.options.filter((e) => e.values).length === selectSection?.options.length && (
                <>
                    <Box mt="28px" mb="24px">
                        {showTitle && <SubText variant="body2">Select</SubText>}
                        {selectSection?.options.map((item: any, index) => (
                            <>
                                {['parent', 'tag_id'].includes(item.name) ? (
                                    <>
                                        <TextField
                                            key={`text-field-select${index}`}
                                            sx={{ mt: 2 }}
                                            label={<FormattedMessage id={item.label} values={{ indicator: '' }} />}
                                            fullWidth
                                            select
                                            value={newFilters.find((f) => f.name === item.name)?.value || ''}
                                            onChange={(e) => {
                                                const removedKeyFilter = newFilters.filter((obj) => obj.name !== item.name);
                                                setNewFilters([...removedKeyFilter, { name: item.name, value: e.target.value }]);
                                            }}
                                        >
                                            {/* {Object.keys(item.values).map((key, value) => (
                                                <MenuItem key={value} value={value}>
                                                    <FormattedMessage id={key} defaultMessage={key} />
                                                </MenuItem>
                                            ))} */}
                                            {item.values.map((value: any) => (
                                                <MenuItem key={value.id} value={value.id}>
                                                    <FormattedMessage id={value.name} defaultMessage={value.name} />
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </>
                                ) : (
                                    <>
                                        <TextField
                                            key={`text-field-select${index}`}
                                            sx={{ mt: 2 }}
                                            label={<FormattedMessage id={item.label} values={{ indicator: '' }} />}
                                            fullWidth
                                            select
                                            value={newFilters.find((f) => f.name === item.name)?.value || ''}
                                            onChange={(e) => {
                                                const removedKeyFilter = newFilters.filter((obj) => obj.name !== item.name);
                                                setNewFilters([...removedKeyFilter, { name: item.name, value: e.target.value }]);
                                            }}
                                        >
                                            {Object.keys(item.values).map((key: string) => (
                                                <MenuItem key={key} value={key}>
                                                    <FormattedMessage id={item.values[key]} defaultMessage={item.values[key]} />
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </>
                                )}
                            </>
                        ))}
                    </Box>
                    <Divider />
                </>
            )}
            {datetimeSection && (
                <>
                    <Box mt="28px" mb="23px">
                        {showTitle && (
                            <SubText variant="body2">
                                <FormattedMessage id="Date" />
                            </SubText>
                        )}
                        {datetimeSection?.options.map((item, index) => (
                            <Box sx={{ mt: 2 }} key={`date-field-${index}`}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label={<FormattedMessage id={item.label} />}
                                        inputFormat="dd-MM-yyyy"
                                        views={['day']}
                                        value={newFilters.find((f) => f.name === item.name)?.value || null}
                                        onChange={(date) => {
                                            if (date) {
                                                const removedKeyFilter = newFilters.filter((obj) => obj.name !== item.name);
                                                setNewFilters([
                                                    ...removedKeyFilter,
                                                    { name: item.name, value: format(new Date(date), 'yyyy-MM-dd') }
                                                ]);
                                            }
                                        }}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </LocalizationProvider>
                            </Box>
                        ))}
                    </Box>
                    <Divider />
                </>
            )}
            <Box mt="65px" mb="37px" display="flex" justifyContent="space-between">
                <ButtonStyled onClick={handleResetFilters} variant="outlined">
                    <FormattedMessage id="reset" />
                </ButtonStyled>
                <ButtonStyled
                    // onClick={() => {
                    //     onClose();
                    //     if (setFilters) {
                    //         setFilters(newFilters);
                    //     }
                    // }}
                    onClick={handleApplyFilters}
                    variant="contained"
                    sx={{ color: theme.palette.background.paper }}
                >
                    <FormattedMessage id="apply" />
                </ButtonStyled>
            </Box>
        </>
    );
};

export default GlobalFilter;
